import React, { useState, useRef, useCallback, useLayoutEffect } from "react";
import "./Composer.css";
import { api } from "./api";
import { IconAttachment, IconClose, IconPlus } from "./Icons";
import { describeError } from "./errors";
import ModelPicker from "./ModelPicker";

// No file-TYPE restriction: "+" uploads land in the environment's files (which
// accept any bytes — Excel, zips, binaries, …) and the agent is just told the
// path, opening them with its own tools. The old image/text/pdf allow-list was
// a leftover from when attachments went through the Anthropic Files API.
// Size cap matches the backend's env-upload limit (environments.MAX_UPLOAD_BYTES)
// so users get an instant friendly error instead of a server 400 after waiting.
const MAX_BYTES = 100 * 1024 * 1024;

export default function Composer({
  onSend, status, onStop, environmentId, disabled, disabledHint, onUploaded,
  models = [], selectedModel = null, onSelectModel,
  modelPickerEnabled = true,
}) {
  const [text, setText] = useState("");
  const [pending, setPending] = useState([]); // {path, filename}
  const [uploadingCount, setUploadingCount] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Grow the box with the draft (newlines are a first-class feature now);
  // CSS min/max-height bound it. ponytail: JS instead of `field-sizing:
  // content` because Safari/Firefox don't ship that yet.
  const fitTextarea = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };
  useLayoutEffect(fitTextarea, [text]);
  // Re-fit when the box gets narrower/wider (sidebar or drawer toggles, window
  // resize) since the same text rewraps to a different line count. The rAF
  // keeps the height write out of the observer callback (avoids the
  // "ResizeObserver loop" error that CRA's dev overlay surfaces).
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let width = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth === width) return;
      width = el.clientWidth;
      requestAnimationFrame(fitTextarea);
    });
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The "+" button uploads straight into the environment's files under an
  // `uploads/` folder (created on demand) — NOT as a Files-API message
  // attachment. On send we tell the agent where the file lives (see handleSend
  // -> onSend's attachedPaths); the file persists in the workspace + Files panel.
  const uploadFiles = useCallback(async (files) => {
    if (!environmentId) {
      setError("Pick an environment before attaching files.");
      return;
    }
    setError(null);
    setUploadingCount((n) => n + files.length);
    for (const f of files) {
      if (f.size > MAX_BYTES) { setError(`File too large: ${f.name} (max 100 MB)`); setUploadingCount((n) => n - 1); continue; }
      const path = `uploads/${f.name}`;
      try {
        await api.uploadEnvironmentFile(environmentId, f, path);
        setPending((p) => [...p.filter((x) => x.path !== path), { path, filename: f.name }]);
        if (onUploaded) onUploaded();
      } catch (e) {
        setError(describeError(e) || "upload failed");
      } finally {
        setUploadingCount((n) => n - 1);
      }
    }
  }, [environmentId, onUploaded]);

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    uploadFiles(Array.from(e.dataTransfer?.files || []));
  };
  const onFileChosen = (e) => uploadFiles(Array.from(e.target.files || []));

  const isRunning = status === "running";

  const handleSend = async () => {
    if (isRunning) return; // can't send while a response is in flight
    const t = text.trim();
    if (!t && pending.length === 0) return;
    const attachedPaths = pending.map((f) => f.path);
    const attachments = pending.map((f) => ({ path: f.path, filename: f.filename }));
    // Clear the draft the moment it's handed off — the bubble renders
    // optimistically right away, and the send promise can take seconds (the
    // backend round-trips Anthropic before replying). A send that reports
    // false (gate cancelled, request failed) puts the draft back.
    const snapshot = pending;
    setText("");
    setPending([]);
    if (await onSend({ text: t, attachedPaths, attachments }) === false) {
      // Prepend rather than overwrite: the user may have started the next
      // message while this one was in flight.
      setText((cur) => (cur ? `${t}\n${cur}` : t));
      setPending((cur) => [...snapshot.filter((f) => !cur.some((c) => c.path === f.path)), ...cur]);
    }
  };

  const handleKey = (e) => {
    if (e.key !== "Enter") return;
    // Enter that confirms an IME composition (Japanese/Chinese/Korean input)
    // belongs to the IME, not to us. 229 is the legacy keyCode for the same.
    if (e.nativeEvent?.isComposing || e.keyCode === 229) return;
    // Enter with ANY modifier (Cmd/Ctrl/Alt/Shift — Win key on Windows is
    // metaKey) inserts a newline. Browsers only do that natively for Shift, so
    // splice it in at the caret ourselves and sync state.
    if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) {
      e.preventDefault();
      const el = e.target;
      el.setRangeText("\n", el.selectionStart, el.selectionEnd, "end");
      setText(el.value);
      return;
    }
    // While running, leave Enter to its default (newline) so the user can keep
    // composing the next message; only plain Enter-to-send once the agent is idle.
    if (!isRunning) {
      e.preventDefault();
      handleSend();
    }
  };

  // Drop the chip AND delete the now-orphaned file from the workspace so the
  // Files panel doesn't keep an upload the user backed out of.
  const removePending = useCallback(async (path) => {
    setPending((p) => p.filter((f) => f.path !== path));
    if (!environmentId) return;
    try {
      await api.deleteEnvironmentFile(environmentId, path);
      if (onUploaded) onUploaded();
    } catch (_) { /* best-effort cleanup */ }
  }, [environmentId, onUploaded]);

  const canSend = !disabled && !isRunning && (text.trim().length > 0 || pending.length > 0);

  return (
    <div
      className="ca-composer-wrapper"
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onDrop={onDrop}
    >
      {error && <div className="ca-composer-error-overlay">{error}</div>}
      <div className="ca-composer-container">
        {pending.length > 0 && (
          <div className="ca-composer-attachments">
            {pending.map((f) => (
              <span key={f.path} className="ca-composer-chip">
                <IconAttachment size={12} />
                <span className="ca-composer-chip-name">{f.filename}</span>
                <button onClick={() => removePending(f.path)} aria-label="Remove attachment">
                  <IconClose size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
        {uploadingCount > 0 && (
          <div className="ca-composer-info">Uploading {uploadingCount}…</div>
        )}
        <textarea
          ref={textareaRef}
          className="ca-composer-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKey}
          placeholder={disabled
            ? (disabledHint || "No agents available yet…")
            : "Message the agent…"}
          disabled={disabled}
          rows={2}
        />
        <div className="ca-composer-buttons">
          <button
            className="ca-composer-icon"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || !environmentId}
            title={environmentId ? "Attach files" : "Pick an environment to attach files"}
            aria-label="Attach files"
          >
            <IconPlus size={14} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={onFileChosen}
          />
          <div className="ca-composer-spacer" />
          {models.length > 0 && (
            <ModelPicker
              models={models}
              value={selectedModel}
              onChange={onSelectModel}
              disabled={!modelPickerEnabled}
            />
          )}
          {isRunning ? (
            <button
              className="ca-composer-icon ca-composer-stop"
              onClick={onStop}
              title="Stop response"
              aria-label="Stop response"
            >
              <span className="ca-composer-stop-icon" />
            </button>
          ) : (
            <button
              className="ca-composer-icon ca-composer-send"
              onClick={handleSend}
              disabled={!canSend}
              title={canSend ? "Send (Enter)" : "Type a message to send"}
              aria-label="Send"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="1.8"
                   strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 19V5M5 12l7-7 7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
